import _ from 'lodash';
import React, { ReactNode } from 'react';
import Selector from 'components/inputs/Selector';
import Typography from '@mui/material/Typography';
import TreeTablePicker from 'components/inputs/TreeTablePicker/TreeTablePicker';
import {
    deserializeListValue,
    optionsToFlatTree,
} from 'components/inputs/TreeTablePicker/helpers';
import {
    checkExtractFilter,
    checkExtractFilterScale,
    valueToOptions,
} from './helpers';
import { EXTRACT_VALUES, DEFAULT_SCALE, DEFAULT_VALUE } from './constants';
import { useTranslation } from 'react-i18next';

type Props = {
    filter: SentenceFilterWithScopesT;
    setFilter: FlowAnyFunction;
    children?: ReactNode;
    isDisabled?: boolean;
};

function ExtractDatePicker({
    children,
    filter,
    setFilter,
    isDisabled,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    // We need to instantiate the constants here, otherwise the JSON translations
    // are not loaded, and the wrong values appear.
    const scaleOptions = _(EXTRACT_VALUES)
        .keys()
        .map((scale) => valueToOptions(scale))
        .value();

    const valuesScaleDict = _(EXTRACT_VALUES)
        .mapValues((values, scale) =>
            values.map((value) => valueToOptions(value, scale)),
        )
        .value();

    const { value, valueScale, scopes } = filter;
    const { shouldResetValue } = checkExtractFilter(valueScale, value);
    const shouldResetScale = !checkExtractFilterScale(valueScale);

    React.useEffect(() => {
        if (shouldResetScale) {
            setFilter({ valueScale: DEFAULT_SCALE, value: DEFAULT_VALUE });
        }
    }, [shouldResetScale]);

    React.useEffect(() => {
        if (shouldResetValue && !shouldResetScale) {
            setFilter({
                value: _.toString(EXTRACT_VALUES[valueScale][0]),
            });
        }
    }, [shouldResetValue, shouldResetScale, valueScale]);

    if (shouldResetScale || (shouldResetValue && valueScale)) {
        return <></>;
    }

    const scopeOptions = 'scopes' in filter ? scopes : [];
    const gridOptions = optionsToFlatTree(valuesScaleDict[valueScale]);
    const deserializedValues = deserializeListValue(
        value,
        valuesScaleDict[valueScale],
        scopeOptions,
    ).map(
        // We update the label of the values to have the name of the month instead of the value (which is an integer)
        // because the function deserializeListValue assign the value to the label
        (selected) => ({
            ...selected,
            label: valuesScaleDict[valueScale].filter(
                (e) => e.value === selected.value,
            )[0].label,
        }),
    );

    gridOptions.children = _.sortBy(gridOptions.children, [
        function (o) {
            return o.id;
        },
    ]);
    return (
        <>
            {children}
            <Typography>{t('sentence:filterLinker.the')}</Typography>

            <Selector
                disabled={isDisabled}
                options={scaleOptions}
                value={valueScale}
                onChange={(newValueScale) =>
                    setFilter({ valueScale: newValueScale })
                }
                fullWidth={false}
            />
            <Typography>{t('sentence:filterLinker.in')}</Typography>
            <TreeTablePicker
                selectedValues={deserializedValues}
                disabled={isDisabled}
                onChange={(newValue) => {
                    const printData = newValue.map((o) => o.value).join('|');
                    setFilter({ value: printData });
                }}
                title={t('sentence:monthsOfTheYear')}
                className="e2e-filter-extract-values"
                gridOptions={gridOptions}
                // @ts-expect-error [TS migration] Was not detected by flow
                scopeOptions={scopeOptions}
                // We had this otherwise the months are going to be sorted by value considered as string
                // that would mean that value 11 goes before 2, meaning november is before february
                doNotSort
            />
        </>
    );
}
export default ExtractDatePicker;
